/**
 * API invitations — côté CGP (auth) + côté public (token URL).
 *
 * Côté CGP : utilise l'API client habituel.
 * Côté public : fetch direct (pas de cookies/sessions, juste le token URL).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { config } from "@/lib/config";
import type {
  InvitationCreate,
  InvitationCreateResponse,
  InvitationRead,
  PersonUpdate,
  PublicInvitationContext,
  PublicRecueilSnapshot,
  PublicRequiredDocument,
} from "./schema";
import { invalidateDossierProgress } from "@/lib/api/dossier-progress";

// ---------------------------------------------------------------------------
// CGP-side
// ---------------------------------------------------------------------------

export const invitationsApi = {
  list: (cfId: string) => api.get<InvitationRead[]>(`/client-files/${cfId}/invitations`),
  create: (cfId: string, payload: InvitationCreate) =>
    api.post<InvitationCreateResponse>(`/client-files/${cfId}/invitations`, payload),
  revoke: (cfId: string, invitationId: string) =>
    api.post<InvitationRead>(`/client-files/${cfId}/invitations/${invitationId}/revoke`),
  // Relance « 1 clic » (US 2.4) : renvoie l'email d'invitation (token
  // régénéré côté backend). 409 en français si révoquée / expirée /
  // complétée ou si une relance est partie il y a moins de 24 h.
  remind: (cfId: string, invitationId: string) =>
    api.post<InvitationRead>(`/client-files/${cfId}/invitations/${invitationId}/remind`),
  // « Marquer comme vu » (file « À faire ») — idempotent ; DELETE annule
  // (toast « Annuler »). Visibilité dossier vérifiée côté serveur.
  markReviewed: (cfId: string, invitationId: string) =>
    api.post<InvitationRead>(`/client-files/${cfId}/invitations/${invitationId}/mark-reviewed`),
  unmarkReviewed: (cfId: string, invitationId: string) =>
    api.delete<InvitationRead>(`/client-files/${cfId}/invitations/${invitationId}/mark-reviewed`),
};

// SUBMITTED exclu : le backend refuse (409) de relancer un dossier complété.
const RELAUNCHABLE_STATUSES: readonly InvitationRead["status"][] = [
  "PENDING",
  "OPENED",
  "VERIFIED",
];

function latestRelaunchable(invitations: InvitationRead[]): InvitationRead | null {
  const latest = invitations
    .filter((inv) => RELAUNCHABLE_STATUSES.includes(inv.status))
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
  return latest ?? null;
}

export async function remindLatestInvitation(cfId: string): Promise<InvitationRead> {
  const invitations = await invitationsApi.list(cfId);
  const latest = latestRelaunchable(invitations);
  if (!latest) {
    throw new Error("Aucune invitation relançable pour ce dossier.");
  }
  if (!latest.sent_to_email) {
    throw new Error("La dernière invitation n'a pas de destinataire email.");
  }

  return invitationsApi.remind(cfId, latest.id);
}

export const invitationKeys = {
  all: ["invitations"] as const,
  list: (cfId: string) => ["invitations", "list", cfId] as const,
};

export function useInvitations(cfId: string | undefined) {
  return useQuery({
    queryKey: cfId ? invitationKeys.list(cfId) : ["invitations", "noop"],
    queryFn: () => invitationsApi.list(cfId as string),
    enabled: !!cfId,
  });
}

export function useCreateInvitation(cfId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InvitationCreate) => invitationsApi.create(cfId, payload),
    onSuccess: () => invalidateDossierProgress(qc, cfId),
  });
}

export function useRevokeInvitation(cfId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => invitationsApi.revoke(cfId, invitationId),
    onSuccess: () => invalidateDossierProgress(qc, cfId),
  });
}

export function useRemindInvitation(cfId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => invitationsApi.remind(cfId, invitationId),
    onSuccess: () => invalidateDossierProgress(qc, cfId),
  });
}

// ---------------------------------------------------------------------------
// Public-side (token-based)
// ---------------------------------------------------------------------------

const publicBase = `${config.apiBaseUrl.replace(/\/$/, "")}${config.apiPrefix}/public/invitations`;

/** Codes machine renvoyés par l'API publique (`detail.code`). */
export const PUBLIC_ERROR_CODES = {
  /** Le code à 6 chiffres a expiré (10 min) : en redemander un. */
  otpExpired: "otp_expired",
  /** Appareil / réseau différent depuis la vérification : refaire le code. */
  otpReverifyRequired: "otp_reverify_required",
} as const;

/**
 * Événement DOM émis quand le serveur exige une nouvelle vérification
 * (changement d'IP, ADR 0018 addendum). La page publique l'écoute et
 * ramène le client à l'étape du code, quel que soit l'appel qui l'a déclenché.
 */
export const PUBLIC_REVERIFY_EVENT = "onpatrimonia:public-invitation-reverify";

/** Erreur de l'API publique : message affichable + statut + code éventuel. */
export class PublicApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = "PublicApiError";
    this.status = status;
    this.code = code;
  }
}

/** Extrait message + code d'une réponse d'erreur FastAPI (`detail`). */
export function parsePublicError(status: number, text: string): PublicApiError {
  let message = `HTTP ${status}`;
  let code: string | null = null;
  try {
    const data = JSON.parse(text) as { detail?: unknown };
    const detail = data?.detail;
    if (typeof detail === "string") {
      message = detail;
    } else if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const obj = detail as { message?: unknown; code?: unknown };
      if (typeof obj.message === "string") message = obj.message;
      else message = JSON.stringify(detail);
      if (typeof obj.code === "string") code = obj.code;
    } else if (detail !== undefined) {
      message = JSON.stringify(detail);
    }
  } catch {
    if (text) message = text;
  }
  return new PublicApiError(message, status, code);
}

async function publicFetch<T>(
  path: string,
  init?: { method?: string; body?: object | null },
): Promise<T> {
  const url = `${publicBase}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  const body =
    init?.body !== undefined && init?.body !== null ? JSON.stringify(init.body) : undefined;
  const resp = await fetch(url, {
    method: init?.method ?? "GET",
    headers,
    ...(body !== undefined ? { body } : {}),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    const error = parsePublicError(resp.status, text);
    if (error.code === PUBLIC_ERROR_CODES.otpReverifyRequired && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(PUBLIC_REVERIFY_EVENT, { detail: error.message }));
    }
    throw error;
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// Helper générique pour CRUD ressources patrimoine via token
function makeResourceApi<TRead, TCreate, TUpdate>(resourceName: string) {
  return {
    list: (token: string) => publicFetch<TRead[]>(`/${token}/${resourceName}`),
    create: (token: string, payload: TCreate) =>
      publicFetch<TRead>(`/${token}/${resourceName}`, {
        method: "POST",
        body: payload as unknown as object,
      }),
    update: (token: string, id: string, payload: TUpdate) =>
      publicFetch<TRead>(`/${token}/${resourceName}/${id}`, {
        method: "PATCH",
        body: payload as unknown as object,
      }),
    delete: (token: string, id: string) =>
      publicFetch<void>(`/${token}/${resourceName}/${id}`, { method: "DELETE" }),
  };
}

export const publicInvitationApi = {
  open: (token: string) => publicFetch<PublicInvitationContext>(`/${token}`),
  /** Sans adresse : le code part à la destination connue (masquée sur la page). */
  requestOtp: (token: string, channel_value?: string) =>
    publicFetch<PublicInvitationContext>(`/${token}/otp/request`, {
      method: "POST",
      body: channel_value ? { channel_value } : {},
    }),
  verifyOtp: (token: string, code: string) =>
    publicFetch<PublicInvitationContext>(`/${token}/otp/verify`, {
      method: "POST",
      body: { code },
    }),
  getSnapshot: (token: string) => publicFetch<PublicRecueilSnapshot>(`/${token}/snapshot`),
  updatePerson: (token: string, payload: PersonUpdate) =>
    publicFetch<{ status: string }>(`/${token}/person`, {
      method: "PATCH",
      body: payload,
    }),
  submit: (token: string) =>
    publicFetch<PublicInvitationContext>(`/${token}/submit`, { method: "POST" }),
  /** Pièces que le conseiller attend de la personne invitée. */
  requiredDocuments: (token: string) =>
    publicFetch<PublicRequiredDocument[]>(`/${token}/required-documents`),

  // Patrimoine — CRUD via token
  realEstate: makeResourceApi<
    import("./schema").RealEstateAssetRead,
    import("./schema").RealEstateAssetCreate,
    import("./schema").RealEstateAssetUpdate
  >("real-estate"),
  financial: makeResourceApi<
    import("./schema").FinancialAssetRead,
    import("./schema").FinancialAssetCreate,
    import("./schema").FinancialAssetUpdate
  >("financial"),
  loans: makeResourceApi<
    import("./schema").LoanRead,
    import("./schema").LoanCreate,
    import("./schema").LoanUpdate
  >("loans"),
  donations: makeResourceApi<
    import("./schema").DonationRead,
    import("./schema").DonationCreate,
    import("./schema").DonationUpdate
  >("donations"),
  insurance: makeResourceApi<
    import("./schema").InsurancePolicyRead,
    import("./schema").InsurancePolicyCreate,
    import("./schema").InsurancePolicyUpdate
  >("insurance"),
  atypical: makeResourceApi<
    import("./schema").AtypicalAssetRead,
    import("./schema").AtypicalAssetCreate,
    import("./schema").AtypicalAssetUpdate
  >("atypical"),
  objectives: makeResourceApi<
    import("./schema").ObjectiveRead,
    import("./schema").ObjectiveCreate,
    import("./schema").ObjectiveUpdate
  >("objectives"),

  // Singletons (1-1)
  getTax: (token: string) => publicFetch<import("./schema").TaxSituationRead>(`/${token}/tax`),
  upsertTax: (token: string, payload: import("./schema").TaxSituationUpsert) =>
    publicFetch<import("./schema").TaxSituationRead>(`/${token}/tax`, {
      method: "PUT",
      body: payload as unknown as object,
    }),
  getWill: (token: string) => publicFetch<import("./schema").WillRead>(`/${token}/will`),
  upsertWill: (token: string, payload: import("./schema").WillUpsert) =>
    publicFetch<import("./schema").WillRead>(`/${token}/will`, {
      method: "PUT",
      body: payload as unknown as object,
    }),

  // Profil investisseur (MiFID II)
  investorProfile: {
    questionnaire: (token: string) =>
      publicFetch<import("./schema").QuestionnaireMetadata>(
        `/${token}/investor-profile/questionnaire`,
      ),
    get: (token: string) =>
      publicFetch<{
        questionnaire_version: string;
        answers: Record<string, unknown>;
        is_submitted: boolean;
      }>(`/${token}/investor-profile`),
    update: (token: string, answers: Record<string, unknown>) =>
      publicFetch<import("./schema").InvestorProfileRead>(`/${token}/investor-profile`, {
        method: "PUT",
        body: { answers },
      }),
    submit: (token: string) =>
      publicFetch<import("./schema").InvestorProfileRead>(`/${token}/investor-profile/submit`, {
        method: "POST",
      }),
    score: (token: string) =>
      publicFetch<import("./schema").ScoreBreakdown>(`/${token}/investor-profile/score`),
  },
};
